import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine, faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDatabase, faChartBar, faRandom, faTrain, faUsers,
    faClock, faDesktop, faBrain
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/TrainRouteOptimisation-Process.png";
import vis from "../../assets/images/use-cases/TrainRouteOptimisation-Dashboard.gif";

const TrainReroutingOptimization = () => {
    return (
        <>
            <SEO
                title="Train Rerouting Optimization: Graph Analytics for Rail Networks | Nebulyx AI"
                description="Optimize train rerouting during disruptions using graph-based analytics. Dijkstra's algorithm calculates optimal alternative routes, minimizing passenger delays and operational costs."
                keywords="train rerouting, rail optimization, Dijkstra algorithm, graph analytics, network operations, incident management, schedule optimization"
                canonicalUrl="https://nebulyx.ai/use-cases/train-rerouting-optimization"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div>
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faTrain}/>
                                            &nbsp;
                                            Transport
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;How do we minimize disruption when incidents occur?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Optimize train rerouting during network disruptions using graph-based analytics to calculate alternative routes, minimize passenger delays, and maintain service continuity.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faUsers} className="stat-icon"/>
                                <span className="stat-value">45K</span>
                                <span className="stat-label">Daily Passengers Protected</span>
                            </Col>
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faClock} className="stat-icon"/>
                                <span className="stat-value">38%</span>
                                <span className="stat-label">Faster Recovery Time</span>
                            </Col>
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">22 Stations</span>
                                <span className="stat-label">Network Coverage</span>
                            </Col>
                        </Row>
                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}>
                                            <span className={"card-icon-wrapper problem me-1"}>
                                                <FontAwesomeIcon icon={faExclamationTriangle}/>
                                            </span>
                                            The Problem
                                        </h3>
                                        <ul className="summary-list">
                                            <li>Manual rerouting decisions during incidents</li>
                                            <li>No real-time visibility of network capacity</li>
                                            <li>Cascading delays from suboptimal routing</li>
                                            <li>Passenger information lags behind decisions</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}>
                                            <span className={"card-icon-wrapper solution me-1"}>
                                                <FontAwesomeIcon icon={faLightbulb}/>
                                            </span>
                                            The Solution
                                        </h3>
                                        <ul className="summary-list">
                                            <li>Graph-based network representation</li>
                                            <li>Dijkstra's shortest path algorithm</li>
                                            <li>Real-time capacity constraint modeling</li>
                                            <li>Automated schedule regeneration</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}>
                                            <span className={"card-icon-wrapper benefits me-1"}>
                                                <FontAwesomeIcon icon={faRocket}/>
                                            </span>
                                            The Benefits
                                        </h3>
                                        <ul className="summary-list">
                                            <li>38% faster incident recovery</li>
                                            <li>Reduced passenger delay minutes</li>
                                            <li>Optimized crew and rolling stock allocation</li>
                                            <li>Automated passenger communications</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Ingests infrastructure topology, timetable data, real-time train positions, incident feeds, and passenger demand to calculate optimal rerouting during disruptions.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Connect Sources</strong>
                                    <span>Network topology, timetables, signalling data, train positions, incident feeds</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Build Graph</strong>
                                    <span>Data Object Graph maps stations, junctions, track segments, and capacity constraints</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Run Scenarios</strong>
                                    <span>Dijkstra's algorithm calculates alternative routes avoiding blocked sections</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Optimise Output</strong>
                                    <span>New schedules, platform assignments, crew adjustments, and passenger notifications</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* Digital Twin Section */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>

                        {/* Dashboard */}
                        <Row className="mb-4">
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faBrain}/>
                                            </div>
                                            <h3>Route Optimization Engine</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Connects infrastructure topology, timetable data, real-time train positions, and incident information to build a complete picture of network state. Calculates optimal alternative routes using graph traversal algorithms while respecting capacity constraints and connection requirements.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt="Train Rerouting Process"/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Network Operations Dashboard</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Provides real-time visibility of all train movements, active incidents, and rerouting recommendations. Enables controllers to visualize alternative paths, assess passenger impact, and approve schedule changes before deployment to operations.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt="Train Controller Dashboard"/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Sources</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Network Infrastructure Topology</li>
                                            <li>Master Timetable Database</li>
                                            <li>Real-time Train Positions (GPS)</li>
                                            <li>Signalling System Feeds</li>
                                            <li>Incident Management System</li>
                                            <li>Crew Rostering System</li>
                                            <li>Passenger Information Feeds</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Network Graph Model</li>
                                            <li>Dijkstra's Shortest Path</li>
                                            <li>Capacity Utilization Analysis</li>
                                            <li>Delay Propagation Prediction</li>
                                            <li>Passenger Impact Assessment</li>
                                            <li>Schedule Conflict Detection</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Single track blockage</li>
                                            <li>Station platform closure</li>
                                            <li>Rolling stock failure</li>
                                            <li>Crew availability shortage</li>
                                            <li>Multi-incident cascade</li>
                                            <li>Peak vs off-peak response</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Discovery</h3>
                                    <p>
                                        By modelling the rail network as a <strong>Data Object Graph</strong>, we can demonstrate that
                                        <strong> re-calculating alternative routes when there is an incident </strong> reduces
                                        controller decision time. Graph traversal also reveals that <strong>delays cascade</strong> from just 3 junction bottlenecks,
                                        enabling targeted infrastructure investment.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Decision Time Reduction</span>
                                        <span className="discovery-value">12min → 90sec</span>
                                        <span className="discovery-note">Pre-computed route library</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Need to Optimize Your Rail Network Operations?</h2>
                            <p>
                                If your network struggles with incident response times and cascading delays,
                                let's build your graph-powered rerouting system together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default TrainReroutingOptimization;